import { Button } from '@repo/ui/components/button';
import { Card, CardContent } from '@repo/ui/components/card';
import { Link, useLocation } from 'react-router';

import lockupUrl from '@/assets/brand/garden-lockup.png';

import { paths } from './paths';

/**
 * A mistyped or stale address.
 *
 * Declared `publicRoute()` deliberately, and the route table says why: sending
 * an unknown URL to sign-in, and then returning somebody to a page that does
 * not exist, is worse than saying it does not exist. So this screen is reached
 * signed in and signed out both, and its copy has to work either way — it never
 * says "the back office", because a signed-out visitor has not got one yet.
 *
 * It renders outside the shell, so it names the organisation itself: a dead end
 * with no identity on it reads as the browser having failed rather than as a
 * page this app meant to show.
 *
 * The address is shown back. Staff report problems by reading them down a
 * phone, and the thing worth reading is the one the router could not match.
 */
export function NotFound() {
  const location = useLocation();

  return (
    <main className="flex min-h-screen justify-center p-6">
      <div className="my-auto w-full max-w-sm">
        <Card className="w-full">
          <CardContent className="space-y-6">
            <div className="space-y-1.5">
              {/* The mark rather than the name in type. This screen can be
                  reached signed out, so it cannot borrow the shell's header —
                  it has to say whose application it is on its own. */}
              <img
                src={lockupUrl}
                alt="Government Botanical Garden Munnar"
                className="h-5 w-auto"
              />
              <h1 className="font-heading text-xl font-medium tracking-tight">Page not found</h1>
              <p className="text-sm text-muted-foreground">
                Nothing here answers to that address. If you followed a link or a bookmark, it is
                out of date.
              </p>
            </div>

            {/* Tabular figures, like every other value in this app that gets
                read out rather than only looked at.

                Full foreground and not `text-muted-foreground`: this is the
                thing somebody reads down a phone, so it is content rather than
                annotation. It also has to be — muted text on a muted fill
                measures 4.39:1 and misses AA, which is the trap in a pairing
                that reads as the obvious one. */}
            <p className="rounded-3xl bg-muted px-3 py-2 text-sm break-all numeric">
              {location.pathname}
            </p>

            <Button className="w-full" render={<Link to={paths.dashboard} />}>
              Go to the start
            </Button>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
